import { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Plus, Trash2, MessageSquare, Bot, User } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { streamAI } from "@/lib/aiStream";
import { PageHeader } from "@/components/StatCard";
import { AiText } from "@/components/AiPanels";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const SUGGEST = ["Berapa total stok per jenis ikan saat ini?", "Batch mana yang harus segera dikeluarkan karena hampir kadaluarsa?",
  "Stok grade A apa saja yang tersedia?", "Ada berapa transaksi yang menunggu kelengkapan?"];

function Bubble({ m, idx }) {
  const mine = m.role === "user";
  return (
    <div className={`flex gap-2 ${mine ? "justify-end" : ""}`} data-testid={`ai-msg-${idx}`}>
      {!mine && <span className="h-8 w-8 shrink-0 rounded-full bg-slate-900 text-sky-300 flex items-center justify-center"><Bot className="h-4 w-4" /></span>}
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${mine ? "bg-sky-600 text-white rounded-br-sm" : "bg-white border border-sky-100 text-slate-800 rounded-bl-sm"}`}>
        {m.content ? (mine ? m.content : <AiText text={m.content} />) : <span className="inline-flex gap-1"><span className="h-2 w-2 rounded-full bg-sky-400 animate-bounce" /><span className="h-2 w-2 rounded-full bg-sky-400 animate-bounce [animation-delay:120ms]" /><span className="h-2 w-2 rounded-full bg-sky-400 animate-bounce [animation-delay:240ms]" /></span>}
      </div>
      {mine && <span className="h-8 w-8 shrink-0 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center"><User className="h-4 w-4" /></span>}
    </div>
  );
}

export default function Assistant() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const loadSessions = () => api.get("/ai/sessions").then((r) => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const open = async (id) => { setSid(id); const r = await api.get(`/ai/sessions/${id}`); setMsgs(r.data); };
  const reset = () => { setSid(null); setMsgs([]); };
  const remove = async (id) => {
    if (!window.confirm("Hapus percakapan ini?")) return;
    try { await api.delete(`/ai/sessions/${id}`); if (id === sid) reset(); loadSessions(); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((p) => [...p, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const fin = await streamAI("/ai/chat", { session_id: sid, message }, (t) => setMsgs((p) => { const c = [...p]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t }; return c; }));
      if (fin.session_id && !sid) { setSid(fin.session_id); loadSessions(); }
    } catch (e) {
      toast.error(e.message);
      setMsgs((p) => p.slice(0, -1));
      if (e.meta?.session_id && !sid) { setSid(e.meta.session_id); loadSessions(); }
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Asisten AI" subtitle="Tanya apa saja tentang stok, kadaluarsa, dan transaksi gudang — dijawab dari data terkini" />
      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        <div className="card-aqua p-3 h-fit min-w-0" data-testid="ai-sessions">
          <Button onClick={reset} className="w-full rounded-full bg-sky-600 hover:bg-sky-700 mb-3" data-testid="btn-ai-new"><Plus className="h-4 w-4 mr-1" /> Percakapan Baru</Button>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {sessions.length === 0 && <p className="text-xs text-slate-400 px-2">Belum ada percakapan.</p>}
            {sessions.map((s, i) => (
              <div key={s.id} className={`group flex items-center gap-2 rounded-lg px-2 py-2 text-sm cursor-pointer ${s.id === sid ? "bg-sky-100 text-sky-900" : "hover:bg-sky-50 text-slate-700"}`} onClick={() => open(s.id)} data-testid={`ai-session-${i}`}>
                <MessageSquare className="h-3.5 w-3.5 shrink-0" /><span className="truncate flex-1">{s.title}</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="sm:opacity-0 sm:group-hover:opacity-100 text-rose-500" data-testid={`btn-ai-delete-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="card-aqua flex flex-col min-h-[60vh] min-w-0" data-testid="ai-chat">
          <div className="flex-1 p-4 space-y-3 overflow-y-auto max-h-[62vh]">
            {msgs.length === 0 && (
              <div className="py-8 text-center">
                <Sparkles className="h-8 w-8 mx-auto text-sky-500" />
                <p className="mt-2 text-sm text-slate-600">Mulai dengan salah satu pertanyaan:</p>
                <div className="mt-4 flex flex-wrap justify-center gap-2">{SUGGEST.map((s, i) => <button key={s} type="button" onClick={() => send(s)} className="text-xs rounded-full border border-sky-200 bg-white px-3 py-1.5 text-sky-800 hover:bg-sky-50 transition-colors" data-testid={`ai-suggest-${i}`}>{s}</button>)}</div>
              </div>
            )}
            {msgs.map((m, i) => <Bubble key={i} m={m} idx={i} />)}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-sky-100 p-3 flex gap-2 items-end">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Ketik pertanyaan, mis. stok cakalang 2-4 kg grade A berapa?" className="resize-none bg-white min-h-[44px]" data-testid="input-ai-message" />
            <Button type="submit" disabled={busy || !text.trim()} className="h-11 rounded-full bg-sky-600 hover:bg-sky-700" data-testid="btn-ai-send"><Send className="h-4 w-4" /></Button>
          </form>
        </div>
      </div>
    </div>
  );
}
